import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { DailyReportService } from '../../../../daily-report.service';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { Ability, subject } from '@casl/ability';
import { takeUntil } from 'rxjs/operators';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivityCodesStationRangeComponent } from '../activity-codes-station-range/activity-codes-station-range.component';
import {
  clearLocationControls,
  linkRawValue,
  patchPickedLocation,
  recordLinkPayload,
} from '../../../../../../../../../../shared/helpers/design-context';

@Component({
    selector: 'app-activity-codes-details-form',
    templateUrl: './activity-codes-details-form.component.html',
    styleUrls: ['./activity-codes-details-form.component.scss'],
    standalone: false
})
export class ActivityCodesDetailsFormComponent implements OnInit, OnDestroy {
  @Input() data: any;
  editable = false;

  form = new FormGroup<any>({
    activityCodesSegment: new FormControl({ value: null, disabled: false }, [Validators.required]),
    activityCodesLocation: new FormControl({ value: null, disabled: true }, [Validators.required]),
    activityCodes: new FormControl({ value: null, disabled: true }, [Validators.required]),
    latitude: new FormControl(null),
    longitude: new FormControl(null),
    comment: new FormControl(null),
    pictures: new FormControl('[]'),
    videos: new FormControl('[]'),
    stationRangeFromStation: new FormControl({ value: null, disabled: false }),
    stationRangeFromOffsetType: new FormControl({ value: null, disabled: false }),
    stationRangeFromOffsetDistance: new FormControl({ value: null, disabled: false }),
    stationRangeToStation: new FormControl({ value: null, disabled: false }),
    stationRangeToOffsetType: new FormControl({ value: null, disabled: false }),
    stationRangeToOffsetDistance: new FormControl({ value: null, disabled: false }),
    alignment: new FormControl({ value: null, disabled: false }),
    map_feature_link: new FormControl(null),
  });

  errorMessages: any = {};
  errorMessageMap = {
    activityCodes: {
      required: 'This field is required.',
    },
    activityCodesSegment: {
      required: 'This field is required.',
    },
    activityCodesLocation: {
      required: 'This field is required.',
    },
  };

  formSubscription: Subscription = new Subscription();

  activityCodeOptions: any = [];
  activityCodesSegmentOptions: any = [];
  activityCodesLocationOptions: any = [];
  filteredActivityCodesSegmentOptions: any = [];
  filteredActivityCodeOptions: any = [];
  filteredActivityCodesLocationOptions: any = [];

  pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: 10000,
  };

  mediaFiles = [];
  videoToBeDeletedFromMuxDb = [];
  displayCommentSection = false;
  displayStationRange: any;
  stationRange: any = {};
  alignment: any;
  selectedActivitiesIds: any = [];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private dailyReportService: DailyReportService,
    private cdRef: ChangeDetectorRef,
    public dialog: MatDialog,
    private ability: Ability,
  ) {

  }


  ngOnInit() {
    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.editable = this.ability.can('create', subject('DailyReport', { ...report }));
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.activityCodesSegmentOptions = data.activityCodesSegmentList;
        this.filteredActivityCodesSegmentOptions = data.activityCodesSegmentList;

        this.activityCodesLocationOptions = data.activityCodesLocationList;
        this.filteredActivityCodesLocationOptions = data.activityCodesLocationList;

        this.activityCodeOptions = data.activityCodesList;
        this.filteredActivityCodeOptions = data.activityCodesList;
      });

    if (this.data?.id) {
      this.displayCommentSection = !!this.data.comment;
      const activityCodes = [];
      let activityCodesSegment: any;
      let activityCodesLocation: any;

      this.data?.lines.forEach(line => {
        activityCodes.push(line?.activity_code?.id);
      });

      if (activityCodes.length > 0) {
        const idList = this.activityCodeOptions?.filter(item => activityCodes?.includes(item?.id));
        activityCodesSegment = idList?.[0]?.segment;
        activityCodesLocation = idList?.[0]?.segmentLocation;

        // tslint:disable-next-line:max-line-length
        this.filteredActivityCodesLocationOptions = this.activityCodesLocationOptions?.filter(item => activityCodesSegment?.includes(item?.segment));
        this.filteredActivityCodeOptions = this.activityCodeOptions?.filter(item => activityCodesSegment?.includes(item?.segment));

        this.form.controls?.['activityCodesLocation']?.enable();
        this.form.controls?.['activityCodes']?.enable();
      }

      if (this.isValidObject(this.data?.station_range?.to) || this.isValidObject(this.data?.station_range?.from)) {
        this.displayStationRange = true;
        this.stationRange = {
          from: {
            station: this.data?.station_range?.from?.station,
            offsetType: this.data?.station_range?.from?.offset_type,
            offsetDistance: this.data?.station_range?.from?.offset_distance,
          },
          to: {
            station: this.data?.station_range?.to?.station,
            offsetType: this.data?.station_range?.to?.offset_type,
            offsetDistance: this.data?.station_range?.to?.offset_distance,
          },
        };
        this.alignment = this.data?.station_range?.alignment;
      }

      this.form.patchValue({
        activityCodes,
        activityCodesSegment,
        activityCodesLocation,
        latitude: this.data?.latitude,
        longitude: this.data?.longitude,
        comment: this.data.comment,
        pictures: JSON.stringify(this.data?.pictures),
        videos: JSON.stringify(this.data?.videos),
        stationRangeFromStation: this.data?.station_range?.from?.station,
        stationRangeFromOffsetType: this.data?.station_range?.from?.offset_type,
        stationRangeFromOffsetDistance: this.data?.station_range?.from?.offset_distance,
        stationRangeToStation: this.data?.station_range?.to?.station,
        stationRangeToOffsetType: this.data?.station_range?.to?.offset_type,
        stationRangeToOffsetDistance: this.data?.station_range?.to?.offset_distance,
        alignment: this.data?.station_range?.alignment,
        map_feature_link: this.data?.map_feature_link ?? null,
      }, { emitEvent: false });
      this.mediaFiles = [...this.data.pictures, ...this.data?.videos];
      this.dailyReportService.issue.next(this.data);
      if (this.data?.lines?.length > 0) {
        const lines = this.data?.lines;
        lines.forEach((line: any) => {
          this.selectedActivitiesIds.push(line?.activity_code);
        });
      }
    } else {
      this.dailyReportService.disableOptionMenu.next(true);
    }

    this.form.controls?.['activityCodesSegment']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.form.controls?.['activityCodesLocation']?.reset();
        this.form.controls?.['activityCodes']?.reset();

        this.filteredActivityCodesLocationOptions = [];
        this.filteredActivityCodeOptions = [];
        this.selectedActivitiesIds = [];

        if (data?.length > 0) {
          this.filteredActivityCodesLocationOptions = this.activityCodesLocationOptions.filter(item => data?.includes(item?.segment));
          this.form.controls?.['activityCodesLocation']?.enable();
        }
      });

    this.form.controls?.['activityCodesLocation']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.form.controls?.['activityCodes']?.reset();

        this.filteredActivityCodeOptions = [];
        this.selectedActivitiesIds = [];

        if (data?.length > 0) {
          this.filteredActivityCodeOptions = this.activityCodeOptions.filter(item => data?.includes(item?.segmentLocation));
          this.form.controls?.['activityCodes']?.enable();
        }
      });

    this.form.controls?.['activityCodes']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.selectedActivitiesIds = [];

        if (data?.length > 0) {
          this.selectedActivitiesIds = this.activityCodeOptions.filter(item => data?.includes(item?.id));
        }
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.save();
      });

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];

        items.forEach((eachItem: any) => {
          if (eachItem?.['fileType'] === 'picture' || eachItem?.['fileType'] === 'document') {
            let pictures: any = this.form.get('pictures')?.value || '[]';
            pictures = JSON.parse(pictures);

            pictures.push(eachItem);
            this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array

          } else if (eachItem?.['fileType'] === 'video') {
            let videos: any = this.form.get('videos')?.value || '[]';
            videos = JSON.parse(videos);

            videos.push(eachItem);
            this.form?.get('videos')?.setValue(JSON.stringify(videos));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array
          }
        });

        this.mediaFiles = [...this.mediaFiles];
      });

    this.dailyReportService.selectedLocation
      .pipe(takeUntil(this.onDestroy))
      .subscribe((location) => {
        // Only the keys the picker sent: an absent link key leaves the link (no location/station controls here).
        patchPickedLocation(this.form, location, { emitEvent: false });
        this.cdRef.detectChanges();
      });

    this.dailyReportService.addComment
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.displayCommentSection = true;
      });

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((res: any) => {
        this.projectsService.isAnyFormDirty.next(true);
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.form) {
          this.delete();
        }
      });

    this.dailyReportService.setStationRange
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.stationRange = data?.stationRange;
        this.alignment = data.alignment;

        this.cdRef.detectChanges();
      });
  }

  save() {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    // A link removed this session is { sheet: null }; an untouched null stays a bare null (R54)
    const values = linkRawValue(this.form);
    const {
      pictures,
      videos,
      activityCodes,
      comment,
      latitude,
      longitude,
      stationRangeFromStation,
      stationRangeFromOffsetType,
      stationRangeFromOffsetDistance,
      stationRangeToStation,
      stationRangeToOffsetType,
      stationRangeToOffsetDistance,
      alignment,
      map_feature_link,
    } = values;

    const tempPictureData = JSON.parse(pictures);
    const tempVideoData = JSON.parse(videos);

    const lines = [];
    activityCodes.forEach((activityCode: any) => {
      lines.push({ activity_code_id: activityCode });
    });

    const payload = {
      ...(this.data?.id ? { id: this.data?.id } : { id: `new_${new Date().getTime()}` }),
      comment,
      pictures: tempPictureData,
      videos: tempVideoData,
      lines,
      latitude,
      longitude,
      map_feature_link: recordLinkPayload(map_feature_link, latitude, longitude),
      station_range: {
        alignment,
        from: {
          station: stationRangeFromStation,
          offset_type: stationRangeFromOffsetType,
          offset_distance: stationRangeFromOffsetDistance,
        },
        to: {
          station: stationRangeToStation,
          offset_type: stationRangeToOffsetType,
          offset_distance: stationRangeToOffsetDistance,
        },
      },
    };

    this.dailyReportService.save.next({
      type: 'activity-codes',
      data: [payload],
    });
  }

  delete() {
    this.dailyReportService.delete.next({
      type: 'activity-codes',
      id: this.data.id,
    });
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture') {
      let pictures = this.getPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos();

      videos = videos?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('videos')?.setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  deleteLocation() {
    clearLocationControls(this.form);
  }

  showLocationSection() {
    const hasGps = this.form?.controls?.latitude?.value !== null &&
      this.form?.controls?.longitude?.value !== null &&
      this.form?.controls?.latitude?.value !== '' &&
      this.form?.controls?.longitude?.value !== '';
    // W50: a sheet-only drawing link has no pin but still shows its card
    return hasGps || !!this.form?.controls?.map_feature_link?.value?.sheet;
  }

  getPictures() {
    const pictures = this.form.controls['pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  getVideos() {
    const videos = this.form.controls['videos']?.value || '[]';
    return JSON.parse(videos);
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  openStationRangeDetails() {
    this.dialog.open(ActivityCodesStationRangeComponent, {
      disableClose: true,
      width: '1200px',
      height: '620px',
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: 'station-range-dialog-panel',
      data: {
        form: this.form,
      },
    });
  }

  checkStationFormData() {
    const stationRangeFromStationValue = this.form.controls?.['stationRangeFromStation'].value;
    const stationRangeFromOffsetTypeValue = this.form.controls?.['stationRangeFromOffsetType'].value;
    const stationRangeFromOffsetDistanceValue = this.form.controls?.['stationRangeFromOffsetDistance'].value;
    const stationRangeToStationValue = this.form.controls?.['stationRangeToStation'].value;
    const stationRangeToOffsetTypeValue = this.form.controls?.['stationRangeToOffsetType'].value;
    const stationRangeToOffsetDistanceValue = this.form.controls?.['stationRangeToOffsetDistance'].value;
    const alignment = this.form.controls?.['alignment'].value;

    return !!(
      stationRangeFromStationValue
      || stationRangeFromOffsetTypeValue
      || stationRangeFromOffsetDistanceValue
      || stationRangeToStationValue
      || stationRangeToOffsetTypeValue
      || stationRangeToOffsetDistanceValue
      || alignment
    );
  }


  isValidObject(obj: any): boolean {
    return obj && Object.values(obj).every(value => value !== null && value !== undefined && value !== '');
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.commonService.updateIsMarkerClick(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

}
